import type {
  FetchResult,
  QueryHookOptions,
  Reference,
  StoreObject,
} from '@apollo/client';
import React from 'react';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type {
  DeleteAccountMutation,
  DeleteAccountMutationVariables,
  GetAccountRecordQuery,
  GetAccountRecordQueryVariables,
  UpdateAccountBillingAddressMutation,
  UpdateAccountBillingAddressMutationVariables,
  UpdateAccountRecordMutation,
  UpdateAccountRecordMutationVariables,
} from 'generated/account.generated';
import {
  useDeleteAccountMutation,
  useGetAccountRecordQuery,
  useGetAccountUsersQuery,
  useListAccountsQuery,
  useUpdateAccountBillingAddressMutation,
  useUpdateAccountRecordMutation,
} from 'generated/account.generated';
import { useListPurchasedPlans } from 'generated/plan.generated';
import type { AccountType } from 'generated/types';

export const useListAccounts = (type: AccountType) => {
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
  });
  const response = useListAccountsQuery({
    variables: {
      input: {
        account_type: type,
      },
    },
    skip: !type,
  });
  const { data, error } = response;
  // Surface the toast from an effect keyed on the error rather than in the
  // render body: a render-body call re-fires on every re-render while `error`
  // stays set, so a failing query could enqueue multiple toasts instead of one.
  React.useEffect(() => {
    if (error) {
      handleError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  return { ...response, data: data?.listAccounts };
};

export const useGetAccount = (
  opts?: QueryHookOptions<GetAccountRecordQuery, GetAccountRecordQueryVariables>
) => {
  const {
    state: { activeProfile },
  } = useSession();
  const account_id = activeProfile?.account_id ?? '';
  const response = useGetAccountRecordQuery({
    ...opts,
    variables: {
      input: {
        account_id,
        ...opts?.variables?.input,
      },
    },
    skip: !(account_id || opts?.variables?.input.account_id) || opts?.skip,
  });
  return response;
};

export const useGetAccountUsers = (account_id?: string) => {
  const {
    state: { activeProfile },
  } = useSession();
  const accountId = account_id ?? activeProfile?.account_id ?? '';
  const response = useGetAccountUsersQuery({
    variables: {
      input: {
        account_id: accountId,
      },
    },
    skip: !accountId,
  });
  return { ...response, data: response.data?.getAccountRecord.users };
};

export function useUpdateAccountBillingAddress(): [
  (
    params: UpdateAccountBillingAddressMutationVariables
  ) => Promise<FetchResult<UpdateAccountBillingAddressMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateBillingAddress',
  });
  const [mutate, { loading: baseLoading }] =
    useUpdateAccountBillingAddressMutation();
  const loading = baseLoading;

  async function updateAccountBillingAddress(
    params: UpdateAccountBillingAddressMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.updateAccountBillingAddress.data) {
          return;
        }
        const storeObject = response.updateAccountBillingAddress
          .data as unknown as StoreObject;
        try {
          cache.modify({
            fields: {
              getAccountRecord: (existing: Reference, { toReference }) => {
                const reference = toReference(storeObject);
                if (existing.__ref === reference?.__ref) {
                  return reference;
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateAccountBillingAddress.data && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateAccountBillingAddress, loading];
}

type UpdateAccountParams = Omit<
  UpdateAccountRecordMutationVariables['input'],
  'account_id'
>;

export function useUpdateAccount(): [
  (
    params: UpdateAccountParams
  ) => Promise<FetchResult<UpdateAccountRecordMutation>>,
  boolean,
] {
  const {
    state: { loggedInProfile },
  } = useSession();
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateAccountRecord',
  });
  const [mutate, { loading: baseLoading }] = useUpdateAccountRecordMutation();
  const loading = baseLoading;

  async function updateAccount(params: UpdateAccountParams) {
    const response = await mutate({
      variables: {
        input: { ...params, account_id: loggedInProfile?.account_id ?? '' },
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateAccountRecord.data && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateAccount, loading];
}

export function useDeleteAccount(): [
  (
    params: DeleteAccountMutationVariables,
    accountUsers: {
      user_id: string;
      vitall_patient?: { vitall_patient_id: string } | null;
    }[]
  ) => Promise<FetchResult<DeleteAccountMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'deleteAccount',
  });
  const [mutate, { loading: baseLoading }] = useDeleteAccountMutation();
  const loading = baseLoading;

  async function deleteAccount(
    params: DeleteAccountMutationVariables,
    accountUsers: {
      user_id: string;
      vitall_patient?: { vitall_patient_id: string } | null;
    }[]
  ) {
    const response = await mutate({
      variables: {
        ...params,
      },
      update: (cache, { data: response }) => {
        if (response?.deleteAccount.success) {
          for (const { user_id, vitall_patient } of accountUsers) {
            if (vitall_patient?.vitall_patient_id) {
              const normalizedVpId = cache.identify({
                __typename: 'VitallPatientModel',
                vitall_patient_id: vitall_patient.vitall_patient_id,
              });
              cache.evict({ id: normalizedVpId });
            }
            const normalizedUserId = cache.identify({
              __typename: 'UserModel',
              user_id,
            });
            cache.evict({ id: normalizedUserId });
          }
          const normalizedId = cache.identify({
            __typename: 'AccountModel',
            account_id: params.input.account_id,
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        }
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.deleteAccount.success && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [deleteAccount, loading];
}

/**
 * Retrieve information about an account's purchased plans
 * @param account_id ID of an account
 * @returns Plan options and limits for the account
 */
export function useAccountPlans(account_id?: string | null) {
  // Get plan options for the account
  const { data, loading } = useListPurchasedPlans({
    variables: { input: { account_id: account_id ?? '' } },
    skip: !account_id,
  });
  return React.useMemo(() => {
    const planOptions = (data?.listPurchasedPlans ?? []).map(
      ({ plan_id, name }) => ({
        k: plan_id,
        v: name,
      })
    );
    const planLimits = (data?.listPurchasedPlans ?? []).reduce<
      Record<string, { total: number; limit: number }>
    >((acc, curr) => {
      acc[curr.plan_id] = { total: curr.user_plans.total, limit: curr.limit };
      return acc;
    }, {});
    return { planOptions, planLimits, loading };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
}
